import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import useSWR from "swr";
import ReactGA from "react-ga4";
import fetch from "auth/FetchInterceptor";
import { END_POINTS } from "constants/APIConstant.js";
import { MESSAGE_TYPE } from "redux/constants/ApiMessageResponse.js";
import { apiResponseMessage } from "redux/actions/ApiMessageResponse.js";
import { microfilmDefaultValues, microfilmSchema } from "./microfilmSchema";
import { buildMicroFilmPayload, extractList } from "./utils";

async function postMicroFilm(data) {
  return fetch.post(END_POINTS.MICROFILM, buildMicroFilmPayload(data));
}

export function useMicrofilmForm() {
  const dir = useSelector(({ theme }) => theme.direction);
  const currentLocale = useSelector(({ theme }) => theme.locale);
  const userInfo = useSelector(({ auth }) => auth);
  const dispatch = useDispatch();

  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(microfilmSchema),
    defaultValues: microfilmDefaultValues,
  });
  const { errors } = formState;

  const getServices = useSWR({ url: END_POINTS.SERVICE_TYPE });
  const getAgency = useSWR({ url: END_POINTS.AGENCY_TYPE });
  const getFilm = useSWR({ url: END_POINTS.FILM_TYPE });
  const getDepartment = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS_DEPARTMENT}=${END_POINTS.MICROFILM_DEPARTMENT_ID}`,
  });
  const getServicesTools = useSWR({
    url: `${END_POINTS.SERVICES_TOOLS}/${END_POINTS.MICROFILM_DEPARTMENT_ID}`,
  });

  useEffect(() => {
    const timeOut = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
    return () => clearTimeout(timeOut);
  }, []);

  useEffect(() => {
    ReactGA.initialize(END_POINTS.TRACKING_ID);
    ReactGA.send({
      hitType: "pageview",
      page: "/microfilm",
      title: "Microfilm Page",
    });
  }, []);

  const onSubmit = async (data) => {
    try {
      const result = await postMicroFilm(data);
      if (result?.state) {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: <FormattedMessage id="sent-successfully" />,
            messageType: MESSAGE_TYPE.SUCCESS,
          })
        );
      } else {
        dispatch(
          apiResponseMessage({
            showMessage: true,
            message: result?.data?.message ? (
              result.data.message
            ) : (
              <FormattedMessage id="timeout-error" />
            ),
            messageType: MESSAGE_TYPE.ERROR,
          })
        );
      }
    } catch (error) {
      dispatch(
        apiResponseMessage({
          showMessage: true,
          message: error?.response?.data?.message ? (
            error.response.data.message
          ) : (
            <FormattedMessage id="timeout-error" />
          ),
          messageType: MESSAGE_TYPE.ERROR,
        })
      );
    }
  };

  const isLoading =
    getDepartment.isLoading ||
    getServicesTools.isLoading ||
    getServices.isLoading ||
    getAgency.isLoading ||
    getFilm.isLoading;

  return {
    dir,
    currentLocale,
    userInfo,
    isLoading,
    register,
    handleSubmit,
    errors,
    onSubmit,
    services: extractList(getServices),
    agencies: extractList(getAgency),
    films: extractList(getFilm),
    getDepartment,
    servicesTools: getServicesTools?.data?.data,
  };
}
